// eslint-disable-next-line no-restricted-imports
import { useConnection } from 'hooks/useConnection'
import { useSwapCallArguments } from 'hooks/useSwapCallArguments'
import { ReactNode, useMemo } from 'react'
import { Trade } from 'state/routing/trades'

import useSendSwapTransaction from './useSendSwapTransaction'

export enum SwapCallbackState {
  INVALID,
  LOADING,
  VALID,
}

interface UseSwapCallbackReturns {
  state: SwapCallbackState
  callback?: () => Promise<{ hash: string }>
  error?: ReactNode
}
interface UseSwapCallbackArgs {
  trade: Trade | undefined
  recipientAddress: string | null | undefined // the address of the recipient of the trade, or null if swap should be returned to sender
}

// returns a function that will execute a swap, if the parameters are all valid
// and the user has approved the slippage adjusted input amount for the trade
export function useSwapCallback({ trade, recipientAddress }: UseSwapCallbackArgs): UseSwapCallbackReturns {
  const { account, chainId, wagmiPublicClient } = useConnection()
  const swapCalls = useSwapCallArguments(trade, recipientAddress)
  const { callback } = useSendSwapTransaction(account, chainId, wagmiPublicClient, trade, swapCalls)
  const recipient = recipientAddress === null ? account : recipientAddress

  return useMemo(() => {
    if (!trade || !wagmiPublicClient || !account || !chainId || !callback) {
      return { state: SwapCallbackState.INVALID, error: <>Missing dependencies</> }
    }
    if (!recipient) {
      if (recipientAddress !== null) {
        return { state: SwapCallbackState.INVALID, error: <>Invalid recipient</> }
      } else {
        return { state: SwapCallbackState.LOADING }
      }
    }

    return {
      state: SwapCallbackState.VALID,
      callback: async () => callback(),
    }
  }, [trade, wagmiPublicClient, account, chainId, callback, recipient, recipientAddress])
}
